import React from "react";
import { Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Add } from "@mui/icons-material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import QuotationLog from "./QuotationLog";
import QuotationDraft from "./QuotationDraft";
import { defaultInnerStyle } from "Common/Components/PageContainer/PageContainer";
import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import { MecordColors } from "Constants/Color";
import { FileText, FileSignature, BadgeCheck, PhoneCall } from "lucide-react";
import { clearAllTableStates } from "utils/clearTableStates";
import { app_modules } from "helpers/apps_modules_list";
import { usePermission } from "hooks/usePermission";

interface TabItem {
  value: string;
  label: string;
}

interface InternalDynamicTabsProps {
  activeTab: string;
  tabs: TabItem[];
  onTabChange: (tabValue: string) => void;
}

const InternalDynamicTabs: React.FC<InternalDynamicTabsProps> = ({
  activeTab,
  tabs,
  onTabChange,
}) => {
  const activeIndex = tabs.findIndex(
    (t) => t.value.toLowerCase() === activeTab?.toLowerCase()
  );

  const handleChange = (_event: React.SyntheticEvent, newIndex: number) => {
    onTabChange(tabs[newIndex].value);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={activeIndex >= 0 ? activeIndex : 0}
        onChange={handleChange}
        sx={{
          minHeight: 30,
          "& .MuiTab-root": {
            fontFamily: "'Poppins', sans-serif",
            textTransform: "none",
            color: "#555555",
            minHeight: 30,
            paddingTop: "2px",
            paddingBottom: "2px",
            transition: "color 0.2s ease",
          },
          "& .MuiTab-root:hover": {
            color: "#d2363a",
          },
          "& .Mui-selected": {
            color: "#d2363a !important",
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "#d2363a",
            height: "3px",
            transition: "all 0.3s ease",
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            label={tab.label}
            sx={{ fontFamily: "'Poppins', sans-serif" }}
          />
        ))}
      </Tabs>
      <Divider sx={{ mt: 0, borderColor: "#c35f61" }} />
    </Box>
  );
};

const QuotationContainer: React.FC = () => {
  document.title = "Service Quotation";
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();

  const { canCreate } = usePermission(
    app_modules?.quotation?.app_name,
    app_modules?.quotation?.modules?.service
  );

  const activeTab = tab || "log";

  const handleCreateQuotation = () => {
    navigate("/quotation/service/add");
  };

  const statuses = [
    {
      label: "Total Quotations",
      icon: <FileText size={16} />,
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      count: 120,
    },
    {
      label: "Active Quotations",
      icon: <FileSignature size={16} />,
      bgColor: "bg-red-50",
      textColor: "text-red-600",
      count: 35,
    },
    {
      label: "Converted to SO",
      icon: <BadgeCheck size={16} />,
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-600",
      count: 50,
    },
    {
      label: "Pending Follow-ups",
      icon: <PhoneCall size={16} />,
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      count: 82,
    },
  ];

  const tabs: TabItem[] = [
    { value: "log", label: "All Quotations" },
    { value: "draft", label: "Draft" },
  ];

  const handleTabChange = (newTabValue: string) => {
    clearAllTableStates();
    navigate(`/quotation/service/${newTabValue}`);
  };

  return (
    <div
      className="page-content px-3 quotation-list-page"
      style={{
        paddingTop: "calc(var(--vz-header-height, 56px) + 4px)",
        paddingBottom: "6px",
        height: "100vh",
        boxSizing: "border-box",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      }}
    >
      <style>
        {`
          .quotation-list-page .quotation-toolbar-title {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            min-height: 34px;
            margin-bottom: 0;
          }

          .quotation-list-page .quotation-toolbar-title h6 {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            line-height: 1.2;
            display: flex;
            align-items: center;
            height: 34px;
          }

          .quotation-list-page .quotation-toolbar-title .btn {
            height: 34px !important;
            min-height: 34px !important;
            padding: 0 12px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            line-height: 1 !important;
            font-size: 13px !important;
          }

          .quotation-list-page .quotation-tabs-wrap {
            margin-top: 0;
            margin-bottom: 4px;
          }

          .quotation-list-page .quotation-search-row {
            margin: 0 !important;
            --bs-gutter-y: 0.25rem;
          }

          .quotation-list-page .quotation-search-row .search-box .form-control.search {
            height: 32px !important;
            min-height: 32px !important;
            padding-top: 0.2rem !important;
            padding-bottom: 0.2rem !important;
            font-size: 13px !important;
          }

          .quotation-list-page .quotation-search-row .custom-export-button,
          .quotation-list-page .quotation-search-row .custom-column-visibility-button {
            height: 32px !important;
            min-height: 32px !important;
            padding: 0 10px !important;
          }

          .quotation-list-page .quotation-search-row > .col-lg-4 {
            flex: 0 0 45%;
            max-width: 45%;
          }
          .quotation-list-page .quotation-search-row > .col-xl-5 {
            flex: 0 0 48%;
            max-width: 48%;
          }
          @media (min-width: 992px) {
            .quotation-list-page .quotation-search-row > [class*="col-lg-8"],
            .quotation-list-page .quotation-search-row > [class*="col-xl-7"] {
              flex: 1 1 auto;
              max-width: none;
              width: auto;
            }
          }

          .quotation-list-page .quotation-tab-content {
            flex: 1 1 auto;
            max-height: 100%;
            min-height: 0;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }

          .quotation-list-page .quotation-listing-body {
            flex: 1 1 auto;
            max-height: 100%;
            min-height: 0;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }

          .quotation-list-page .quotation-table-scroll {
            flex: 0 1 auto;
            max-height: 100%;
            min-height: 0;
          }

          /* Hug content when few rows; scroll only when it exceeds available space */
          .quotation-list-page .quotation-table-scroll .table-responsive.table-card {
            flex: 0 1 auto;
            max-height: 100%;
            min-height: 0;
            margin: 0 !important;
            padding: 0;
            overflow: auto !important;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
          }

          .quotation-list-page .quotation-table-scroll .table-responsive .table {
            border-collapse: separate;
            border-spacing: 0;
            margin-bottom: 0;
            width: 100%;
          }

          .quotation-list-page .quotation-table-scroll .table thead th,
          .quotation-list-page .quotation-table-scroll .table tbody td {
            padding: 10px 12px !important;
            vertical-align: middle;
          }

          .quotation-list-page .quotation-table-scroll .table thead th:first-child,
          .quotation-list-page .quotation-table-scroll .table tbody td:first-child {
            padding-left: 14px !important;
          }

          .quotation-list-page .quotation-table-scroll .table thead th:last-child,
          .quotation-list-page .quotation-table-scroll .table tbody td:last-child {
            padding-right: 14px !important;
          }

          .quotation-list-page .quotation-table-scroll > .row {
            flex-shrink: 0;
            margin-top: 0.5rem !important;
            margin-bottom: 0 !important;
            padding: 4px 6px 8px !important;
          }

          .quotation-list-page .quotation-table-scroll .table-responsive thead th {
            position: sticky;
            top: 0;
            z-index: 3;
            background-color: ${MecordColors?.LightThemeBGColor || "#f8e8e8"} !important;
          }

          .quotation-list-page .quotation-table-scroll .table-responsive thead .sticky-first-column {
            z-index: 12 !important;
            left: 0;
          }

          .quotation-list-page .quotation-table-scroll .table-responsive thead .sticky-column {
            z-index: 12 !important;
            right: 0;
          }

          .quotation-list-page .quotation-table-scroll .table-responsive tbody .sticky-first-column {
            z-index: 11;
          }

          .quotation-list-page .quotation-table-scroll .table-responsive tbody .sticky-column {
            z-index: 10;
          }
        `}
      </style>

      <div className="flex-shrink-0">
        <AnalyticsCards statuses={statuses} />
      </div>

      <div
        className="d-flex flex-column"
        style={{
          ...defaultInnerStyle,
          padding: "10px 14px 10px",
          flex: "1 1 0",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div className="flex-shrink-0">
          <div className="quotation-toolbar-title">
            <h6>Service Quotations</h6>
            {canCreate && (
              <Button
                variant="primary"
                onClick={handleCreateQuotation}
                className="d-flex align-items-center gap-2"
                style={{
                  backgroundColor: MecordColors.PrimaryButtonBGColor,
                  borderColor: MecordColors.PrimaryButtonBGColor,
                  borderRadius: "8px",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor =
                    MecordColors.PrimaryButtonHoverBGColor;
                  e.currentTarget.style.borderColor =
                    MecordColors.PrimaryButtonHoverBGColor;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor =
                    MecordColors.PrimaryButtonBGColor;
                  e.currentTarget.style.borderColor =
                    MecordColors.PrimaryButtonBGColor;
                }}
              >
                <Add fontSize="small" />
                Create Service Quotation
              </Button>
            )}
          </div>

          <div className="quotation-tabs-wrap">
            <InternalDynamicTabs
              activeTab={activeTab}
              tabs={tabs}
              onTabChange={handleTabChange}
            />
          </div>
        </div>

        <div className="quotation-tab-content">
          {activeTab === "log" && <QuotationLog />}
          {activeTab === "draft" && <QuotationDraft />}
        </div>
      </div>
    </div>
  );
};

export default QuotationContainer;
